import React from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { FullPageCard } from "component/shared/full-page-card";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { Switch } from "component/shared/switch";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { DashboardLayout } from "component/layout/dashboard";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { RootState } from "redux/store";
import { PageProps } from "shared/page-props";
import { styles } from "./style";

// MAX-3629 — copy per Figma "iHomefinder Refresh Q2 2026" (node 262-7962).
const COPY = {
	pageDescription:
		"Set the default values for the mortgage calculator on your website, including loan term, interest rate, down payment, and monthly tax and insurance estimates.",
	sectionTitle: "Default Settings",
	bankrateUrl: "https://www.bankrate.com/mortgages/mortgage-rates/",
	includeToggle: "Include in monthly payment calculation",
	tooltips: {
		// Mortgage Amount tooltip copy was not present in the Figma; provisional wording —
		// confirm with product/design before release.
		mortgageAmount:
			"Set the default purchase price shown to visitors. They can adjust this in the mortgage calculator.",
		downPayment:
			"Set the default down payment percentage shown to visitors. This is calculated as a percentage of the listing's asking price.",
		monthlyTax:
			"Set the default monthly tax rate shown to visitors. This is calculated as a percentage of the asking price divided by 12 months.",
		monthlyInsurance:
			"Monthly insurance is calculated as a percentage of the total asking price divided by 12 months.",
	},
};

const TERM_OPTIONS = [30, 15, 5];

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface State {
	defaultMortgageAmount: string; // raw digits, no commas
	defaultTerm: number;
	defaultInterestRate: string;
	downPaymentPercentage: string;
	monthlyTaxPercentage: string;
	includeMonthlyTax: boolean;
	monthlyInsurance: string;
	includeMonthlyInsurance: boolean;
	saving: boolean;
	submitted: boolean;
	snackbarOpen: boolean;
}

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({ saveUser }, dispatch);

const decimalToPercent = (value: number) => parseFloat((value * 100).toFixed(4));
const percentToDecimal = (value: number) => value / 100;

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = this.stateFromUser(props.user);
	}

	private stateFromUser(user: User): State {
		return {
			defaultMortgageAmount:
				user.defaultMortgageAmount != null ? String(user.defaultMortgageAmount) : "",
			defaultTerm: user.defaultTerm,
			defaultInterestRate: String(decimalToPercent(user.defaultInterestRate)),
			downPaymentPercentage: String(decimalToPercent(user.downPaymentPercentage)),
			monthlyTaxPercentage: String(decimalToPercent(user.monthlyTaxPercentage)),
			includeMonthlyTax: user.includeMonthlyTax,
			monthlyInsurance: String(decimalToPercent(user.monthlyInsurance)),
			includeMonthlyInsurance: user.includeMonthlyInsurance,
			saving: false,
			submitted: false,
			snackbarOpen: false,
		};
	}

	// Term dropdown offers 30 / 15 / 5; if the client has a stored value outside that set
	// (e.g. a Canadian 25-year amortization), surface it as an extra option so we never
	// silently overwrite it or render a blank select.
	private termOptions(): number[] {
		const stored = this.props.user.defaultTerm;
		return TERM_OPTIONS.includes(stored) ? TERM_OPTIONS : [stored, ...TERM_OPTIONS];
	}

	private formatAmount(raw: string): string {
		return raw ? Number(raw).toLocaleString("en-US") : "";
	}

	private percentError(value: string, label: string): string | undefined {
		const n = Number(value);
		if (value.trim() === "" || isNaN(n) || n < 0 || n > 100) {
			return `${label} should be a number between 0 and 100.`;
		}
		return undefined;
	}

	private getErrors() {
		const { defaultMortgageAmount, defaultInterestRate, downPaymentPercentage } = this.state;
		const { monthlyTaxPercentage, monthlyInsurance } = this.state;
		const amount = Number(defaultMortgageAmount);
		return {
			defaultMortgageAmount:
				defaultMortgageAmount.trim() === "" || isNaN(amount) || amount <= 0
					? "Enter a mortgage amount."
					: undefined,
			defaultInterestRate: this.percentError(defaultInterestRate, "Interest rate"),
			downPaymentPercentage: this.percentError(downPaymentPercentage, "Down payment"),
			monthlyTaxPercentage: this.percentError(monthlyTaxPercentage, "Monthly tax"),
			monthlyInsurance: this.percentError(monthlyInsurance, "Monthly insurance"),
		};
	}

	private isDirty(): boolean {
		const u = this.props.user;
		const s = this.state;
		return (
			(parseInt(s.defaultMortgageAmount, 10) || 0) !== (u.defaultMortgageAmount ?? 0) ||
			s.defaultTerm !== u.defaultTerm ||
			Number(s.defaultInterestRate) !== decimalToPercent(u.defaultInterestRate) ||
			Number(s.downPaymentPercentage) !== decimalToPercent(u.downPaymentPercentage) ||
			Number(s.monthlyTaxPercentage) !== decimalToPercent(u.monthlyTaxPercentage) ||
			s.includeMonthlyTax !== u.includeMonthlyTax ||
			Number(s.monthlyInsurance) !== decimalToPercent(u.monthlyInsurance) ||
			s.includeMonthlyInsurance !== u.includeMonthlyInsurance
		);
	}

	private handleSave = () => {
		const errors = this.getErrors();
		this.setState({ submitted: true });
		if (Object.values(errors).some(Boolean)) {
			return;
		}
		const { saveUser, user } = this.props;
		const s = this.state;
		saveUser({
			user: {
				...user,
				defaultMortgageAmount: parseInt(s.defaultMortgageAmount, 10) || 0,
				defaultTerm: s.defaultTerm,
				defaultInterestRate: percentToDecimal(Number(s.defaultInterestRate)),
				downPaymentPercentage: percentToDecimal(Number(s.downPaymentPercentage)),
				monthlyTaxPercentage: percentToDecimal(Number(s.monthlyTaxPercentage)),
				includeMonthlyTax: s.includeMonthlyTax,
				monthlyInsurance: percentToDecimal(Number(s.monthlyInsurance)),
				includeMonthlyInsurance: s.includeMonthlyInsurance,
			},
		});
		this.setState({ saving: true, snackbarOpen: true });
	};

	private handleCancel = () => {
		this.setState(this.stateFromUser(this.props.user));
	};

	private numberField(opts: {
		label: string;
		value: string;
		onChange: (value: string) => void;
		tooltip?: string;
		adornment?: string;
		error?: string;
	}): React.ReactNode {
		const { classes = {} } = this.props;
		const { submitted } = this.state;
		const showError = submitted && !!opts.error;
		return (
			<Mui.Box className={classes.fieldGroup}>
				<FormField
					label={opts.label}
					tooltip={opts.tooltip}
					value={opts.value}
					onChange={(event) => opts.onChange(event.target.value)}
					error={showError}
					helperText={showError ? opts.error : undefined}
					endAdornment={opts.adornment}
				/>
			</Mui.Box>
		);
	}

	public render() {
		const { classes = {}, user, authError, authLoading } = this.props;
		const s = this.state;
		const errors = this.getErrors();
		const dirty = this.isDirty();
		const title = "Mortgage Calculator";

		return (
			<DashboardLayout
				title={title}
				header={
					<Mui.Box className={classes.headerRow}>
						<Mui.Typography variant="h1">{title}</Mui.Typography>
						{user.baseUrl && (
							<PageHeaderLink href={user.baseUrl}>View Mortgage Calculator</PageHeaderLink>
						)}
					</Mui.Box>
				}
			>
				<Mui.Box mb={4}>
					<Mui.Typography variant="body2" className={classes.introText}>
						{COPY.pageDescription}
					</Mui.Typography>
				</Mui.Box>
				<FullPageCard>
					<Mui.Typography variant="h6" className={classes.sectionHeading}>
						{COPY.sectionTitle}
					</Mui.Typography>
					<Mui.Typography variant="body2" className={classes.sectionDescription}>
						These values will pre-populate the mortgage calculator on your listing detail pages.
						Visitors can adjust them as needed. Reference current rates at{" "}
						<Mui.Link href={COPY.bankrateUrl} target="_blank" rel="noopener noreferrer">
							Bankrate
						</Mui.Link>
						.
					</Mui.Typography>

					{this.numberField({
						label: "Mortgage Amount",
						tooltip: COPY.tooltips.mortgageAmount,
						value: this.formatAmount(s.defaultMortgageAmount),
						onChange: (value) =>
							this.setState({ defaultMortgageAmount: value.replace(/[^\d]/g, "") }),
						error: errors.defaultMortgageAmount,
					})}

					<Mui.Box className={classes.fieldGroup}>
						<FormSelectField
							label="Term"
							value={s.defaultTerm}
							onChange={(value) => this.setState({ defaultTerm: Number(value) })}
							options={this.termOptions()}
							valueExtractor={(term) => term}
							labelExtractor={(term) => `${term} years`}
						/>
					</Mui.Box>

					{this.numberField({
						label: "Interest Rate",
						value: s.defaultInterestRate,
						onChange: (value) => this.setState({ defaultInterestRate: value }),
						adornment: "%",
						error: errors.defaultInterestRate,
					})}

					{this.numberField({
						label: "Down Payment",
						tooltip: COPY.tooltips.downPayment,
						value: s.downPaymentPercentage,
						onChange: (value) => this.setState({ downPaymentPercentage: value }),
						adornment: "%",
						error: errors.downPaymentPercentage,
					})}

					{this.numberField({
						label: "Monthly Tax",
						tooltip: COPY.tooltips.monthlyTax,
						value: s.monthlyTaxPercentage,
						onChange: (value) => this.setState({ monthlyTaxPercentage: value }),
						adornment: "%",
						error: errors.monthlyTaxPercentage,
					})}
					<Mui.Box className={classes.toggleRow}>
						<Switch
							checked={s.includeMonthlyTax}
							onChange={(value) => this.setState({ includeMonthlyTax: value })}
							label={COPY.includeToggle}
							labelBold={false}
						/>
					</Mui.Box>

					{this.numberField({
						label: "Monthly Insurance",
						tooltip: COPY.tooltips.monthlyInsurance,
						value: s.monthlyInsurance,
						onChange: (value) => this.setState({ monthlyInsurance: value }),
						adornment: "%",
						error: errors.monthlyInsurance,
					})}
					<Mui.Box className={classes.toggleRow}>
						<Switch
							checked={s.includeMonthlyInsurance}
							onChange={(value) => this.setState({ includeMonthlyInsurance: value })}
							label={COPY.includeToggle}
							labelBold={false}
						/>
					</Mui.Box>
				</FullPageCard>
				<SettingsStickyFooter
					dirty={dirty}
					saving={s.saving}
					authError={authError}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
				/>
				<Mui.Snackbar
					open={s.snackbarOpen && !authLoading && !authError}
					message="Settings saved"
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false, saving: false })}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<MuiAlert
						severity="success"
						onClose={() => this.setState({ snackbarOpen: false, saving: false })}
					>
						Settings saved successfully!
					</MuiAlert>
				</Mui.Snackbar>
				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
			</DashboardLayout>
		);
	}
}

export const RefreshedSettingsMortgageCalculator = Router.withRouter(
	withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles)
);
